/**
 * @file sweet-alert.ts
 * @module app/modules/_common/utils/sweet-alert
 * @description Keyed notification helper. The default channel is toaster or swal-basic.
 * arg[1], arg[2], arg[3] in that script are the values passed to notify.
 * Swal is sweetalert2-react-content, so title, html, and button text can be React elements.
 * A string title passed to notify is shown as plain text, not HTML.
 * @link https://sweetalert2.github.io/
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.4.0
 * @version 2.1.2
 */

import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { error, info, success, warning } from '../../../../Global/toast';
import { fetchConfigurationSettingByKey } from '../../../../utils/helper.method';
import { CONFIGURATION_SETTING_KEYS } from '../../../../utils/configurationSettingKeys';

const BUILTIN_SCRIPT = `Swal.fire({
  toast: true,
  position: 'top-end',
  showConfirmButton: false,
  timer: 3000,
  timerProgressBar: true,
  icon: arg[1] || 'success',
  title: arg[2] || 'Success',
  didOpen: (toastEl) => {
    toastEl.onmouseenter = Swal.stopTimer;
    toastEl.onmouseleave = Swal.resumeTimer;
  }
});`;

const toastApi = { success, error, warning, info };

const swalForScripts = withReactContent(Swal).mixin({
  heightAuto: false,
  buttonsStyling: false,
  customClass: {
    confirmButton: 'btn btn-primary',
    cancelButton: 'btn btn-light',
    denyButton: 'btn btn-danger',
  },
});

/**
 * Builds the 1-based arg list a notification script reads.
 * args[0] becomes arg[1], args[1] becomes arg[2], and so on.
 * @param {unknown[]} args Values passed to notify, in call order.
 * @returns {unknown[]} Sparse array whose index 1 is the first value.
 */
const toArgList = (args: unknown[]) => {
  const arg: unknown[] = [];
  args.forEach((value, index) => {
    arg[index + 1] = value;
  });
  return arg;
};

/**
 * Replaces arg[1], arg[2], ... for text and numbers.
 * Objects, including React elements, stay as arg[n] so SweetAlert can render them.
 * Indexes are 1-based. A missing index becomes the identifier undefined.
 * @param {string} template Script from the notification textarea.
 * @param {unknown[]} args Values passed to notify, in order.
 * @returns {string} Script ready to run.
 */
export const applyNotificationArgs = (template: string, args: unknown[]) => {
  return template.replace(/\barg\[(\d+)\]/g, (match, indexText: string) => {
    const index = Number(indexText);
    if (!Number.isInteger(index) || index < 1 || index > args.length) {
      return 'undefined';
    }

    const value = args[index - 1];
    if (typeof value === 'undefined') {
      return 'undefined';
    }

    if (value !== null && typeof value === 'object') {
      return match;
    }

    return JSON.stringify(value);
  });
};

/**
 * Reports whether a notify argument should be written onto the popup.
 * Empty strings, null, and undefined are skipped. Objects, including React elements, count as present.
 * @param {unknown} value One notify argument.
 * @returns {boolean} True when the value should replace a script field.
 */
const hasDisplayValue = (value: unknown) => {
  if (value === undefined || value === null) {
    return false;
  }
  if (typeof value === 'string') {
    return value.trim() !== '';
  }
  return true;
};

/**
 * SweetAlert surface used by notification scripts.
 * fire shows a popup. mixin returns another surface with extra default options.
 */
type SwalLike = {
  fire: (options?: Record<string, unknown>) => unknown;
  mixin: (options?: Record<string, unknown>) => SwalLike;
};

/**
 * Puts the notify arguments on Swal.fire.
 * args[0] is the icon, args[1] the title, args[2] the detail (arg[1], arg[2], arg[3] in the script).
 * A hardcoded title in the script does not replace those values.
 * mixin() is wrapped the same way. Other methods, such as showValidationMessage and stopTimer, are forwarded.
 * @param {SwalLike} base React-aware Swal, or a mixin of it.
 * @param {unknown[]} args Values passed to notify, in order.
 * @returns {SwalLike} Swal whose fire() shows the notify arguments.
 */
const bindNotificationSwal = (base: SwalLike, args: unknown[]) => {
  /**
   * Copies the script options and writes icon, title, and detail from args.
   * A string passed to fire() is used as the title only when args do not already supply one.
   * A string title from args is written as plain text (titleText), never as HTML, so text from
   * other users cannot inject markup. A React element title is kept as title.
   * Detail is written to text unless the script set html.
   * @param {Record<string, unknown> | string} [options] Options object, or a title string.
   * @returns {Record<string, unknown>} Options passed to the real Swal.fire.
   */
  const applyPayload = (options?: Record<string, unknown> | string) => {
    const next: Record<string, unknown> = options && typeof options === 'object'
      ? { ...options }
      : {};
    if (typeof options === 'string' && !hasDisplayValue(next.title)) {
      next.title = options;
    }

    const [icon, title, detail] = args;
    if (typeof icon === 'string' && icon.trim()) {
      next.icon = icon;
    }
    if (hasDisplayValue(title)) {
      if (typeof title === 'string') {
        // Notify callers pass data that other people control (a chat group name, a sender
        // name, an API message). SweetAlert renders `title` as HTML, so a string goes in as
        // plain text. Dropping the script's own title also drops the same text it may have
        // received through arg[n]. React elements still use `title`.
        next.titleText = title;
        delete next.title;
      } else {
        next.title = title;
      }
    }
    if (hasDisplayValue(detail) && next.html == null) {
      next.text = detail;
    }
    return next;
  };

  const api = {
    /**
     * Shows the popup after icon, title, and detail from args are applied.
     * @param {Record<string, unknown> | string} [options] Script options, or a title string.
     * @returns {unknown} Result of Swal.fire.
     */
    fire: (options?: Record<string, unknown> | string) => base.fire(applyPayload(options)),
    /**
     * Returns a mixin that still applies icon, title, and detail on fire().
     * @param {Record<string, unknown>} [mixinOptions] Extra SweetAlert defaults such as toast and position.
     * @returns {SwalLike} Wrapped mixin.
     */
    mixin: (mixinOptions?: Record<string, unknown>) => bindNotificationSwal(
      base.mixin(mixinOptions || {}),
      args,
    ),
  };

  return new Proxy(api, {
    /**
     * Returns fire and mixin from this wrapper, and every other SweetAlert method from the base instance.
     * @param {object} target Wrapper that owns fire and mixin.
     * @param {PropertyKey} prop Requested property.
     * @param {unknown} receiver Proxy receiver.
     * @returns {unknown} Wrapped method, or the base property.
     */
    get(target, prop, receiver) {
      if (Object.prototype.hasOwnProperty.call(target, prop)) {
        return Reflect.get(target, prop, receiver);
      }
      const value = (base as Record<PropertyKey, unknown>)[prop];
      return typeof value === 'function' ? value.bind(base) : value;
    },
  });
};

/**
 * Runs a notification script with the React-aware Swal and toast in scope.
 * @param {string} template Script from the notification textarea.
 * @param {unknown[]} args Values passed to notify, in order.
 * @returns {unknown} Whatever the script returns.
 */
export const runNotificationScript = (
  template: string,
  args: unknown[],
  callback: () => Promise<unknown> = () => Promise.resolve(),
) => {
  const source = applyNotificationArgs(template, args);
  const runner = new Function('Swal', 'toast', 'arg', 'callback', source);

  return runner(
    bindNotificationSwal(swalForScripts as unknown as SwalLike, args),
    toastApi,
    toArgList(args),
    callback,
  );
};

/**
 * Loads one configuration setting as text.
 * A non-empty saved value wins. Otherwise the XML default is returned.
 * A failed request returns an empty string.
 * @param {string} settingKey Configuration key.
 * @returns {Promise<string>} Saved value, XML default, or ''.
 */
const readSettingText = async (settingKey: string) => {
  try {
    const record = await fetchConfigurationSettingByKey(settingKey);
    const value = record?.value;
    if (value !== undefined && value !== null && String(value).trim() !== '') {
      return String(value);
    }
    return String(record?.defaultValue ?? '');
  } catch (_error) {
    return '';
  }
};

const SWAL_BASIC = 'swal-basic';
const PREVIEW_ARGS = ['success', `Lorem Ipsum is simply dummy text of the printing and typesetting industry. Lorem Ipsum has been the industry's standard dummy text ever since the 1500s.`];
const SOUND_NONE = 'nosound';
export const NOTIFICATION_SOUND_SUFFIX = '--Sound';

/**
 * Sound setting stored beside a notification script.
 * @param {string} settingKey Script setting key.
 * @returns {string} Key such as notificationAutoRoutePlan--Sound.
 */
export const notificationSoundKey = (settingKey: string) => `${settingKey}${NOTIFICATION_SOUND_SUFFIX}`;

/**
 * Reports whether a key is the default sound or a per-event sound key.
 * Per-event sound keys end with --Sound.
 * @param {string} settingKey Configuration key being previewed or played.
 * @returns {boolean} True when the key stores a sound file name.
 */
const isNotificationSoundKey = (settingKey: string) => (
  settingKey === CONFIGURATION_SETTING_KEYS.KEY__NOTIFICATION_DEFAULT_SOUND
  || settingKey.endsWith(NOTIFICATION_SOUND_SUFFIX)
);

/**
 * Builds the public URL for a sound file served by the uTrade API.
 * @param {string} fileName File name under static_files/sounds.
 * @returns {string} Absolute or root-relative URL.
 */
const notificationSoundUrl = (fileName: string) => {
  const base = String(process.env.REACT_APP_BASE_URL || '/').replace(/\/?$/, '/');
  return `${base}static_files/sounds/${encodeURIComponent(fileName)}`;
};

let activeSound: HTMLAudioElement | null = null;

/**
 * Plays one sound file and stops any clip already playing.
 * nosound, none, an empty name, and names outside letters, numbers, dot, underscore, and hyphen play nothing.
 * @param {string} fileName Selected file name.
 * @returns {void}
 */
const playSoundFile = (fileName: string) => {
  if (activeSound) {
    activeSound.pause();
    activeSound = null;
  }

  const name = String(fileName || '').trim();
  if (!name || name === SOUND_NONE || name === 'none' || !/^[\w.-]+$/.test(name)) {
    return;
  }

  const audio = new Audio(notificationSoundUrl(name));
  activeSound = audio;
  void audio.play().catch(() => undefined);
};

/**
 * Plays the sound chosen in a notification sound dropdown, once.
 * @param {string} assigned Selected file name. "nosound" plays nothing.
 * @returns {void}
 */
export const playNotificationSoundChoice = (assigned: string) => {
  playSoundFile(assigned);
};

/**
 * Shows the existing toast when the channel is toaster or a script throws.
 * args[0] selects success, error, warning, or info. Later text arguments are joined into the body.
 * Objects are skipped. With no text, the body is Success.
 * @param {unknown[]} args Values passed to notify, in order.
 * @returns {void}
 */
const fireToaster = (args: unknown[]) => {
  const icon = typeof args[0] === 'string' ? args[0] : 'success';
  const title = [args[1], ...args.slice(2)]
    .filter((value) => value !== undefined && value !== null && typeof value !== 'object')
    .map((value) => String(value))
    .filter(Boolean)
    .join('\n') || 'Success';

  if (icon === 'error') {
    error(title);
    return;
  }
  if (icon === 'warning') {
    warning(title);
    return;
  }
  if (icon === 'info') {
    info(title);
    return;
  }
  success(title);
};

/**
 * Runs a notification script and falls back to the toaster when it throws.
 * @param {string} script Script from a setting or the builtin toast script.
 * @param {unknown[]} args Values passed to notify, in order.
 * @returns {Promise<unknown>} Script result, or undefined after the toaster fallback.
 */
const runScriptSafely = async (
  script: string,
  args: unknown[],
  callback?: () => Promise<unknown>,
) => {
  try {
    return runNotificationScript(script, args, callback);
  } catch (caught) {
    console.error('Notification script failed.', caught);
    fireToaster(args);
    return undefined;
  }
};

/**
 * Unsaved notification values from the settings form, used by Try me.
 */
export type NotificationPreviewDraft = {
  defaultType?: string;
  basicScript?: string;
  eventScript?: string;
  defaultSound?: string;
  sounds?: Record<string, string>;
};

/**
 * Picks the sound to preview from the settings form, before it is saved.
 * A non-empty per-event choice wins, including nosound. An empty choice uses the default sound.
 * @param {string} settingKey Row being previewed.
 * @param {NotificationPreviewDraft} draft Current form values.
 * @returns {string} File name, nosound, or ''.
 */
const soundFromDraft = (settingKey: string, draft: NotificationPreviewDraft) => {
  const specific = draft.sounds?.[notificationSoundKey(settingKey)];
  if (specific !== undefined && String(specific).trim() !== '') {
    return specific;
  }
  if (isNotificationSoundKey(settingKey)) {
    return draft.sounds?.[settingKey] || draft.defaultSound || '';
  }
  return draft.defaultSound || '';
};

/**
 * Plays the sound stored for a notification key.
 * A non-empty `${settingKey}--Sound` value wins, including nosound. An empty value uses the default sound.
 * @param {string} settingKey Notification script key.
 * @returns {Promise<void>}
 */
export const playNotificationSound = (settingKey: string) => {
  void playSoundForSetting(settingKey);
};

const playSoundForSetting = async (settingKey: string) => {
  const specific = await readSettingText(notificationSoundKey(settingKey));
  if (specific) {
    playSoundFile(specific);
    return;
  }

  playSoundFile(await readSettingText(CONFIGURATION_SETTING_KEYS.KEY__NOTIFICATION_DEFAULT_SOUND));
};

/**
 * Shows a sample notification from the values currently on the settings form.
 * @param {string} settingKey Row being previewed.
 * @param {NotificationPreviewDraft} draft Current form values.
 * @returns {Promise<unknown>} Script result, when a script runs.
 */
export const previewNotification = (settingKey: string, draft: NotificationPreviewDraft) => {
  if (settingKey !== CONFIGURATION_SETTING_KEYS.KEY__NOTIFICATION_SWAL_BASIC_TYPE) {
    playSoundFile(soundFromDraft(settingKey, draft));
  }
  if (isNotificationSoundKey(settingKey)) {
    return undefined;
  }
  const basicScript = (draft.basicScript || '').trim() || BUILTIN_SCRIPT;

  if (settingKey === CONFIGURATION_SETTING_KEYS.KEY__NOTIFICATION_SWAL_BASIC_TYPE) {
    return runScriptSafely(basicScript, PREVIEW_ARGS);
  }

  if (
    settingKey ===
    CONFIGURATION_SETTING_KEYS.KEY__NOTIFICATION_SWAL_BASIC_TYPE_WITH_DEFAULT_SOUND
  ) {
    const script = (draft.eventScript || '').trim() || BUILTIN_SCRIPT;
    return runScriptSafely(script, PREVIEW_ARGS);
  }

  if (
    settingKey !== CONFIGURATION_SETTING_KEYS.KEY__NOTIFICATION_DEFAULT_TYPE
    && settingKey !== CONFIGURATION_SETTING_KEYS.KEY__NOTIFICATION_SWAL_BASIC_TYPE
  ) {
    const eventScript = (draft.eventScript || '').trim();
    if (eventScript) {
      return runScriptSafely(eventScript, PREVIEW_ARGS);
    }
  }

  if ((draft.defaultType || '').trim() === SWAL_BASIC) {
    return runScriptSafely(basicScript, PREVIEW_ARGS);
  }

  fireToaster(PREVIEW_ARGS);
  return undefined;
};

/**
 * Loads the Swal basic script.
 * A non-empty saved value wins. Otherwise the builtin top-right toast script is used.
 * @returns {Promise<string>} Script to run for swal-basic.
 */
const resolveBasicScript = async (
  settingKey: string = CONFIGURATION_SETTING_KEYS.KEY__NOTIFICATION_SWAL_BASIC_TYPE
) => {
  const script = (
    await readSettingText(settingKey)
  ).trim();
  return script || BUILTIN_SCRIPT;
};

/**
 * Shows the notification stored for one event key.
 * A non-empty event script runs as written. An empty script follows notificationDefaultType:
 * swal-basic runs the Swal basic script, and any other channel uses the toaster.
 * @param {string} eventSettingKey Notification script key.
 * @param {unknown[]} args Values passed to notify, in order.
 * @param {boolean} [withSound=false] Play `${eventSettingKey}--Sound`, or the default sound when that key is empty.
 * @returns {Promise<unknown>} Script result, or undefined when the toaster runs.
 */
const fireForEvent = async (
  eventSettingKey: string,
  args: unknown[],
  withSound = false,
  callback?: () => Promise<unknown>,
) => {
  if (withSound) {
    void playSoundForSetting(eventSettingKey);
  }
  const eventScript = (await readSettingText(eventSettingKey)).trim();
  if (eventScript) {
    return runScriptSafely(eventScript, args, callback);
  }

  const channel = (
    await readSettingText(CONFIGURATION_SETTING_KEYS.KEY__NOTIFICATION_DEFAULT_TYPE)
  ).trim();

  if (channel === SWAL_BASIC) {
    return runScriptSafely(await resolveBasicScript(), args);
  }

  fireToaster(args);
  return undefined;
};

/**
 * Runs the notification stored at settingKey.
 * When withSound is true, plays `${settingKey}--Sound`, or the default sound when that key is empty.
 * @param {string} settingKey Notification script key.
 * @param {unknown[]} [args] Values available as arg[1], arg[2], arg[3], and so on.
 * @param {boolean} [withSound=false] Play the matching sound.
 * @returns {Promise<unknown>} Script result, when a script runs.
 */
export const notifyByKey = (
  settingKey: string,
  args: unknown[] = [],
  withSound = false,
  callback?: () => Promise<unknown>,
) => {
  if (settingKey === CONFIGURATION_SETTING_KEYS.KEY__NOTIFICATION_SWAL_BASIC_TYPE) {
    return resolveBasicScript().then((script) => runScriptSafely(script, args));
  }

  if (
    settingKey ===
    CONFIGURATION_SETTING_KEYS.KEY__NOTIFICATION_SWAL_BASIC_TYPE_WITH_DEFAULT_SOUND
  ) {
    if (withSound) {
      void playSoundForSetting(settingKey);
    }
    return resolveBasicScript(settingKey).then((script) =>
      runScriptSafely(script, args),
    );
  }

  return fireForEvent(settingKey, args, withSound, callback);
};

